import React, { useState, useEffect } from 'react';
import { X, User, MapPin, CreditCard, Phone, Mail, Globe, Save, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

// Country list
const COUNTRIES = [
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: 'FR', name: 'France', flag: '🇫🇷' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: 'HT', name: 'Haiti', flag: '🇭🇹' },
  { code: 'JM', name: 'Jamaica', flag: '🇯🇲' },
  { code: 'TT', name: 'Trinidad and Tobago', flag: '🇹🇹' },
  { code: 'BB', name: 'Barbados', flag: '🇧🇧' },
  { code: 'MX', name: 'Mexico', flag: '🇲🇽' },
  { code: 'BR', name: 'Brazil', flag: '🇧🇷' },
  { code: 'NG', name: 'Nigeria', flag: '🇳🇬' },
  { code: 'GH', name: 'Ghana', flag: '🇬🇭' },
  { code: 'KE', name: 'Kenya', flag: '🇰🇪' },
  { code: 'ZA', name: 'South Africa', flag: '🇿🇦' },
  { code: 'IN', name: 'India', flag: '🇮🇳' },
  { code: 'PH', name: 'Philippines', flag: '🇵🇭' },
  { code: 'OTHER', name: 'Other', flag: '🌍' }
].sort((a, b) => a.name.localeCompare(b.name));

// Always use tradepayflow.emergent.host for bonbot.biz custom domain
const getApiUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'bonbot.biz' || hostname === 'www.bonbot.biz') {
    return 'https://bonbot.biz/api';
  }
  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  return `${backendUrl}/api`;
};

const API = getApiUrl();

const UserProfileModal = ({ isOpen, onClose, user, onProfileUpdate }) => {
  const [activeTab, setActiveTab] = useState('view'); // 'view' or 'edit'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Form state
  const [name, setName] = useState('');
  const [country, setCountry] = useState('');
  const [mailingStreet, setMailingStreet] = useState('');
  const [mailingCity, setMailingCity] = useState('');
  const [mailingState, setMailingState] = useState('');
  const [mailingZip, setMailingZip] = useState('');
  const [billingStreet, setBillingStreet] = useState('');
  const [billingCity, setBillingCity] = useState('');
  const [billingState, setBillingState] = useState('');
  const [billingZip, setBillingZip] = useState('');
  const [billingCountry, setBillingCountry] = useState('');
  const [billingPhone, setBillingPhone] = useState('');
  const [billingSameAsMailing, setBillingSameAsMailing] = useState(false);

  // Initialize form with user data
  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setCountry(user.country || '');
      
      // Mailing address
      if (user.mailing_address) {
        setMailingStreet(user.mailing_address.street || '');
        setMailingCity(user.mailing_address.city || '');
        setMailingState(user.mailing_address.state || '');
        setMailingZip(user.mailing_address.zip_code || '');
      }
      
      // Billing address
      if (user.billing_address) {
        setBillingStreet(user.billing_address.street || '');
        setBillingCity(user.billing_address.city || '');
        setBillingState(user.billing_address.state || '');
        setBillingZip(user.billing_address.zip_code || '');
        setBillingCountry(user.billing_address.country || '');
        setBillingPhone(user.billing_address.phone || '');
      }
      
      setBillingSameAsMailing(user.billing_same_as_mailing || false);
    }
  }, [user]);

  const handleSave = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('guestToken');
      
      // Update profile
      const profileData = {
        name,
        country,
        mailing_street: mailingStreet,
        mailing_city: mailingCity,
        mailing_state: mailingState,
        mailing_zip: mailingZip,
        mailing_country: country
      };
      
      await fetch(`${API}/guest/update-profile?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profileData)
      });
      
      // Update billing address
      const billingData = {
        email: user.email,
        billing_street: billingSameAsMailing ? mailingStreet : billingStreet,
        billing_city: billingSameAsMailing ? mailingCity : billingCity,
        billing_state: billingSameAsMailing ? mailingState : billingState,
        billing_zip: billingSameAsMailing ? mailingZip : billingZip,
        billing_country: billingSameAsMailing ? country : billingCountry,
        billing_phone: billingPhone,
        same_as_mailing: billingSameAsMailing
      };
      
      const billingResponse = await fetch(`${API}/guest/update-billing-address`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(billingData)
      });
      
      const billingResult = await billingResponse.json();
      
      if (billingResult.success) {
        // Update local storage
        const updatedUser = {
          ...user,
          name,
          country,
          mailing_address: {
            street: mailingStreet,
            city: mailingCity,
            state: mailingState,
            zip_code: mailingZip,
            country: country
          },
          billing_address: {
            street: billingSameAsMailing ? mailingStreet : billingStreet,
            city: billingSameAsMailing ? mailingCity : billingCity,
            state: billingSameAsMailing ? mailingState : billingState,
            zip_code: billingSameAsMailing ? mailingZip : billingZip,
            country: billingSameAsMailing ? country : billingCountry,
            phone: billingPhone
          },
          billing_same_as_mailing: billingSameAsMailing
        };
        
        localStorage.setItem('guestUser', JSON.stringify(updatedUser));
        
        if (onProfileUpdate) {
          onProfileUpdate(updatedUser);
        }
        
        toast.success('Profile updated successfully!');
        setActiveTab('view');
      } else {
        setError(billingResult.message || 'Failed to update profile');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const getCountryName = (code) => {
    const country = COUNTRIES.find(c => c.code === code);
    return country ? `${country.flag} ${country.name}` : code;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-slate-900 border border-white/20 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="relative p-4 border-b border-white/10 bg-gradient-to-r from-blue-600/20 to-purple-600/20">
          <button
            onClick={onClose}
            className="absolute right-4 top-4 p-2 rounded-full hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-xl font-bold">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{user?.name}</h2>
              <p className="text-sm text-gray-400">{user?.email}</p>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => setActiveTab('view')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'view' 
                  ? 'bg-white/20 text-white' 
                  : 'text-gray-400 hover:text-white hover:bg-white/10'
              }`}
            >
              View Profile
            </button>
            <button
              onClick={() => setActiveTab('edit')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'edit' 
                  ? 'bg-white/20 text-white' 
                  : 'text-gray-400 hover:text-white hover:bg-white/10'
              }`}
            >
              Edit Profile
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          {activeTab === 'view' ? (
            /* View Mode */
            <div className="space-y-4">
              {/* Account Info */}
              <div className="bg-white/5 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
                  <User className="w-4 h-4 text-blue-400" />
                  Account Information
                </h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-500">Name</span>
                    <p className="text-white">{user?.name || '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Email</span>
                    <p className="text-white">{user?.email || '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Country</span>
                    <p className="text-white">{user?.country ? getCountryName(user.country) : '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Status</span>
                    <p className={`font-medium ${user?.status === 'approved' ? 'text-green-400' : 'text-yellow-400'}`}>
                      {user?.status?.replace('_', ' ')?.toUpperCase() || '-'}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Mailing Address */}
              <div className="bg-white/5 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-green-400" />
                  Mailing Address
                </h3>
                {user?.mailing_address?.street ? (
                  <div className="text-sm text-gray-400">
                    <p className="text-white">{user.mailing_address.street}</p>
                    <p>{user.mailing_address.city}, {user.mailing_address.state} {user.mailing_address.zip_code}</p>
                    <p>{user.mailing_address.country}</p>
                  </div>
                ) : (
                  <p className="text-sm text-yellow-400">No mailing address set</p>
                )}
              </div>
              
              {/* Billing Address */}
              <div className="bg-white/5 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-purple-400" />
                  Billing Address
                  {user?.billing_same_as_mailing && (
                    <span className="text-xs bg-gray-500/20 text-gray-400 px-2 py-0.5 rounded">Same as mailing</span>
                  )}
                </h3>
                {user?.billing_address?.street ? (
                  <div className="text-sm text-gray-400">
                    <p className="text-white">{user.billing_address.street}</p>
                    <p>{user.billing_address.city}, {user.billing_address.state} {user.billing_address.zip_code}</p>
                    <p>{user.billing_address.country}</p>
                    {user.billing_address.phone && (
                      <p className="flex items-center gap-1 mt-2 text-blue-400">
                        <Phone className="w-3 h-3" /> {user.billing_address.phone}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-yellow-400">No billing address set</p>
                )}
              </div>
              
              <Button
                onClick={() => setActiveTab('edit')}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
              >
                Edit Profile
              </Button>
            </div>
          ) : (
            /* Edit Mode */
            <div className="space-y-4">
              {/* Name & Country */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Name</label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="bg-white/10 border-white/20 text-white"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Country</label>
                  <Select value={country} onValueChange={setCountry}>
                    <SelectTrigger className="bg-white/10 border-white/20 text-white">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700 max-h-[200px]">
                      {COUNTRIES.map((c) => (
                        <SelectItem key={c.code} value={c.code} className="text-white hover:bg-white/10">
                          {c.flag} {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Mailing Address */}
              <div className="border-t border-white/10 pt-4">
                <h3 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-green-400" />
                  Mailing Address
                </h3>
                <div className="space-y-2">
                  <Input
                    value={mailingStreet}
                    onChange={(e) => setMailingStreet(e.target.value)}
                    placeholder="Street Address"
                    className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      value={mailingCity}
                      onChange={(e) => setMailingCity(e.target.value)}
                      placeholder="City"
                      className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                    />
                    <Input
                      value={mailingState}
                      onChange={(e) => setMailingState(e.target.value)}
                      placeholder="State"
                      className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                    />
                  </div>
                  <Input
                    value={mailingZip}
                    onChange={(e) => setMailingZip(e.target.value)}
                    placeholder="ZIP Code"
                    className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                  />
                </div>
              </div>
              
              {/* Billing Address */}
              <div className="border-t border-white/10 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-gray-300 flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-purple-400" />
                    Billing Address
                  </h3>
                  <label className="flex items-center gap-2 text-xs text-gray-400">
                    <input
                      type="checkbox"
                      checked={billingSameAsMailing}
                      onChange={(e) => setBillingSameAsMailing(e.target.checked)}
                      className="rounded border-gray-600 bg-gray-700"
                    />
                    Same as mailing
                  </label>
                </div>
                
                {!billingSameAsMailing && (
                  <div className="space-y-2 mb-3">
                    <Input
                      value={billingStreet}
                      onChange={(e) => setBillingStreet(e.target.value)}
                      placeholder="Street Address"
                      className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        value={billingCity}
                        onChange={(e) => setBillingCity(e.target.value)}
                        placeholder="City"
                        className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                      />
                      <Input
                        value={billingState}
                        onChange={(e) => setBillingState(e.target.value)}
                        placeholder="State"
                        className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        value={billingZip}
                        onChange={(e) => setBillingZip(e.target.value)}
                        placeholder="ZIP Code"
                        className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                      />
                      <Select value={billingCountry} onValueChange={setBillingCountry}>
                        <SelectTrigger className="bg-white/10 border-white/20 text-white">
                          <SelectValue placeholder="Country" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700 max-h-[200px]">
                          {COUNTRIES.map((c) => (
                            <SelectItem key={c.code} value={c.code} className="text-white hover:bg-white/10">
                              {c.flag} {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
                
                {/* Phone (always visible) */}
                <div>
                  <label className="text-xs text-gray-400 mb-1 block flex items-center gap-1">
                    <Phone className="w-3 h-3" /> Phone Number
                  </label>
                  <Input
                    type="tel"
                    value={billingPhone}
                    onChange={(e) => setBillingPhone(e.target.value)}
                    placeholder="Your phone number"
                    className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                  />
                </div>
              </div>
              
              {error && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                  {error}
                </div>
              )}
              
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setActiveTab('view')}
                  className="flex-1 border-white/20 text-white hover:bg-white/10"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={loading}
                  className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white"
                >
                  {loading ? 'Saving...' : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfileModal;
